import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { SaleReport } from '@interfaces/api/responses/my-sale';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import { taxUrl } from '../helper';
import styles from '../popin.module.scss';
import { PopinView } from './common';

const getSafeSaleReportUrl = (filePath: string) => {
  if (!filePath) {
    return '';
  }

  if (filePath.startsWith('/') && !filePath.startsWith('//')) {
    return filePath;
  }

  try {
    const saleReportUrl = new URL(filePath);
    if (saleReportUrl.protocol === 'https:' || saleReportUrl.protocol === 'http:') {
      return saleReportUrl.href;
    }
  } catch {
    return '';
  }

  return '';
};

const SaleReportView: React.FC<{
  closePopin: () => void;
  rootSaleReport: string;
  saleReports: SaleReport[];
}> = ({ closePopin, rootSaleReport, saleReports }) => {
  const [saleReport, setSaleReport] = useState(rootSaleReport);
  const safeSaleReportUrl = getSafeSaleReportUrl(saleReport);

  const { t } = useTranslation('orders-sales');
  const { t: tA11y } = useTranslation('a11y');
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');
  return (
    <PopinView
      closePopin={closePopin}
      title={t('MY_ACCOUNT.ORDERS_SALES.LIST.TAX_SOCIAL_OBLI')}
    >
      <div className={styles.ordersSales__popin__saleReport}>
        <div className={styles.ordersSales__popin__saleReport__desc}>
          {t('MY_ACCOUNT.ORDERS_SALES.TAX.DESC')}{' '}
          <a
            href={taxUrl}
            className={styles.ordersSales__popin__saleReport__desc__link}
            rel={'noreferrer'}
            target={'_blank'}
            onClick={() => {
              sendAnalyticEvent('timeline_learn_more', { label: 'sales' });
            }}
          >
            {t('MY_ACCOUNT.ORDERS_SALES.TAX.LEARN_MORE')}.
          </a>
        </div>
        <div className={styles.ordersSales__popin__saleReport__form}>
          <div className={styles.ordersSales__popin__saleReport__form__container}>
            <div className={styles.ordersSales__popin__saleReport__form__field}>
              {t('MY_ACCOUNT.ORDERS_SALES.TAX.YOUR_INCOME_SUMMARY')}{' '}
            </div>
            <div className={styles.ordersSales__popin__select}>
              <select onChange={(e) => setSaleReport(e.target.value)}>
                {saleReports.map(({ filePath, year }) => {
                  return (
                    <option
                      key={filePath}
                      value={filePath}
                    >
                      {year}
                    </option>
                  );
                })}
              </select>
              <Icon
                name="arrow-head-down"
                className={styles.ordersSales__popin__select__icon}
                ariaLabel={tA11y('A11Y.ICON.DROPDOWN_DOWN')}
              />
            </div>
          </div>
          <a
            href={safeSaleReportUrl || undefined}
            aria-disabled={!safeSaleReportUrl}
            rel={'noreferrer'}
            target={'_blank'}
            className={clsx('vc-btn vc-btn--medium vc-btn--outline', styles.ordersSales__popin__saleReport__form__btn)}
            onClick={(event) => {
              if (!safeSaleReportUrl) {
                event.preventDefault();
                return;
              }
              const currentYear = saleReports.find(({ filePath }) => filePath === saleReport);
              sendAnalyticEvent('timeline_download_income_summary', {
                label: 'sales',
                property: currentYear ? currentYear.year : '',
              });
            }}
          >
            <Icon
              name="download-default"
              className={styles.ordersSales__popin__saleReport__form__btn__icon__svg}
              ariaHidden
            />
            {t('MY_ACCOUNT.ORDERS_SALES.TAX.DOWNLOAD')}
          </a>
        </div>
      </div>
    </PopinView>
  );
};

export default SaleReportView;
